import { useRef, useState } from "react";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";

interface FileResponse {
  readonly data: BlobPart;
}

interface MissingCompanyError extends Error {
  readonly errorMessage: "MissingCompany";
}

const createMissingCompanyError = (): MissingCompanyError => {
  const errorMessage: "MissingCompany" = "MissingCompany";
  return Object.assign(new Error("MissingCompany"), {
    errorMessage,
  });
};

export const usePDFViewer = () => {
  const [companyState] = useCompanyStore();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fileUrlRef = useRef<string | null>(null);
  const latestRequestIdRef = useRef(0);
  const [, monthSalaryActions] = useMonthSalaryStore();

  const viewFile = async (endpoint: Promise<FileResponse>, type: string) => {
    if (!companyState.company?.id) return;

    const requestId = ++latestRequestIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const response = await endpoint;

      const blob = new Blob([response.data], { type });
      const url = window.URL.createObjectURL(blob);

      // A period switch may have started another request while this one was
      // loading. Never let the earlier response replace the current file.
      if (requestId !== latestRequestIdRef.current) {
        window.URL.revokeObjectURL(url);
        return null;
      }

      if (fileUrlRef.current) {
        window.URL.revokeObjectURL(fileUrlRef.current);
      }

      fileUrlRef.current = url;
      setFileUrl(url);
      setFileContent(blob);
      setIsLoading(false);

      return url;
    } catch {
      if (requestId !== latestRequestIdRef.current) return null;
      setError("Failed to load file");
      setIsLoading(false);
      return null;
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;

    document.body.appendChild(a);
    a.click();

    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const prepareExport = async (
    month: number,
    type: "SIE" | "Hogia",
    year: number,
  ) => {
    const companyId = companyState.company?.id;
    if (!companyId) {
      throw createMissingCompanyError();
    }

    const res =
      type === "Hogia"
        ? await monthSalaryActions.generateHogiaFile(companyId, year, month)
        : await monthSalaryActions.generateSIEFile(companyId, year, month);

    const blob: Blob =
      res.data instanceof Blob
        ? res.data
        : new Blob([res.data], {
            type: type === "Hogia" ? "application/zip" : "text/plain",
          });

    const ext = type === "Hogia" ? "zip" : "se";
    const filename = `${type}_${year}-${month}.${ext}`;

    downloadBlob(blob, filename);
    return true;
  };

  const downloadFile = (filename: string) => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cleanupFile = () => {
    // Invalidate in-flight requests too; otherwise an older period can put
    // its file back after the viewer has moved to a period without a file.
    latestRequestIdRef.current += 1;
    if (fileUrlRef.current) window.URL.revokeObjectURL(fileUrlRef.current);
    fileUrlRef.current = null;
    setFileUrl(null);
    setFileContent(null);
    setIsLoading(false);
  };

  return {
    viewFile,
    downloadFile,
    cleanupFile,
    fileUrl,
    fileContent,
    isLoading,
    error,
    prepareExport,
  };
};
